import { publicHrefForProfileLink } from '@elevator/socialite';
import { SocialIconLink, SocialMediaLink, SizeVariant } from '@elevator/ui';
import Box from '@mui/material/Box';
import useStyles from './styles';
import clsx from 'clsx';

/**
 * SocialMediaGroup component displays a group of social media icon links with spacing.
 * Renders SocialIconLink components for each social media link with variant-specific padding
 * between items. Used in UserCard and Sidebar layouts.
 *
 * @param props - Component props
 * @param props.socialMedia - Array of social media links with type and link URL
 * @param props.variant - Size variant for the icons (small, medium, large)
 * @param props.rest - Additional props passed to SocialIconLink (e.g., color)
 * @returns SocialMediaGroup component or null if socialMedia array is empty
 */
const SocialMediaGroup = ({
  socialMedia,
  variant,
  className,
  ...rest
}: SocialMediaGroupPropTypes) => {
  const { classes } = useStyles();

  if (!socialMedia || socialMedia.length === 0) {
    return null;
  }

  const variantPaddingMap: Record<SizeVariant, string> = {
    small: classes.smallPadding,
    medium: classes.mediumPadding,
    large: classes.largePadding,
  };

  const paddingClass = variantPaddingMap[variant];

  return (
    <Box className={clsx(classes.root, className)} data-component="SocialMediaGroup">
      {socialMedia.map((socialMediaLink, index) => {
        // Use combination of type and link as unique key
        const key = `${socialMediaLink.type}-${socialMediaLink.link}`;
        // Only apply padding to items that are not the last
        const shouldApplyPadding = index !== socialMedia.length - 1;
        const iconType = socialMediaLink.type === 'twitter' ? 'x' : socialMediaLink.type;

        return (
          <Box
            key={key}
            className={clsx(classes.iconWrapper, shouldApplyPadding && paddingClass)}
          >
            <SocialIconLink
              href={publicHrefForProfileLink(socialMediaLink.type, socialMediaLink.link)}
              type={iconType}
              variant={variant}
              {...rest}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export interface SocialMediaGroupPropTypes {
  /** Array of social media links with type and link URL */
  socialMedia: SocialMediaLink[];
  /** Size variant for the icons (small, medium, large) */
  variant: SizeVariant;
  /** Optional className to apply to the root element */
  className?: string;
}

export default SocialMediaGroup;
